import { Box, Group, Stack, UnstyledButton, useMantineTheme } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';

import { Text } from '~/domains/design-system/Text';
import type { CryptoCatalogNetwork } from '~/services/wallet/interfaces/crypto-direct.interface';
import dt from '~/testing';

import { BackButton } from '../BackButton';
import { AssetIcon } from './AssetIcon';
import { NetworkIcon } from './NetworkIcon';

type CryptoAssetListProps = {
  network: CryptoCatalogNetwork;
  onSelect: (symbol: string) => void;
  onBack: () => void;
};

/** Screen 2 of the gated crypto deposit flow: pick a Whop asset within the chosen network. */
export function CryptoAssetList({ network, onSelect, onBack }: CryptoAssetListProps) {
  const theme = useMantineTheme();
  const { t } = useTranslation('payments');

  return (
    <Stack spacing={theme.other.spacing._16}>
      <Group spacing={theme.other.spacing._8}>
        {/* EDGE-32128 (folded into EDGE-32114): this gutter back returns to the network list; give
            the icon-only button the approved contextual name (was a raw "Back"). */}
        <BackButton
          onClick={onBack}
          aria-label={t('deposits.cryptoDirect.breadcrumb.backToNetworks')}
        />
        <Text variant="subhead-medium">{network.name}</Text>
      </Group>
      <Box
        component="ul"
        role="list"
        p={0}
        m={0}
        sx={{
          listStyle: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: theme.other.spacing._8,
        }}
        data-test-id={dt.payments.deposits.cryptoDirect.assetList}
      >
        {network.assets.map((asset) => (
          <Box component="li" key={asset.symbol} p={0} m={0}>
            <UnstyledButton
              aria-label={asset.symbol}
              onClick={() => onSelect(asset.symbol)}
              data-test-id={dt.payments.deposits.cryptoDirect.assetRow}
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: theme.other.spacing._8,
                padding: theme.other.spacing._12,
                borderRadius: theme.radius.md,
                border: `1px solid ${theme.colors.gray2[1]}`,
              }}
            >
              <AssetIcon
                symbol={asset.symbol}
                iconUrl={asset.iconUrl}
                networkName={network.name}
                networkIconUrl={network.iconUrl}
              />
              <Text variant="subhead-medium">{asset.symbol}</Text>
            </UnstyledButton>
          </Box>
        ))}
      </Box>
    </Stack>
  );
}
